import React, { useState } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  ScrollView, 
  StyleSheet, 
  KeyboardAvoidingView,
  Linking,
  Alert,
  Platform 
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LoginHeroHeader } from '@/components/LoginHeroHeader';
import { strings } from '@/constants/residents/strings';
import { COLORS, APP_CONSTANTS } from '@/constants/residents';
import { AppConfig } from '@/constants/residents/config';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { getUserTypeOptions, isResident } from '@/types/residents/userType';
import { UserType } from '@/types/residents/user';
import { showAlert } from '@/utils/common/alert';

type UserSelectionNavigationProp = StackNavigationProp<AuthStackParamList, 'UserSelection'>;

export const UserSelectionScreen: React.FC = () => {
  const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null);
  const navigation = useNavigation<UserSelectionNavigationProp>();
  
  const userTypeOptions = getUserTypeOptions();

  const handleUserTypeSelection = (userType: UserType) => {
    setSelectedUserType(userType);
  };

  const handleContinue = () => {
    if (!selectedUserType) return;

    if (isResident(selectedUserType)) {
      // Navigate to signup screen for residents
      navigation.navigate('Signup');
    } else {
      // Open website for non-residents
      handleOpenWebsite();
    }
  };

  const handleOpenWebsite = () => {
    const websiteUrl = AppConfig.URLS.APP; // Environment-aware URL matching iOS
    
    Linking.canOpenURL(websiteUrl)
      .then((supported) => {
        if (supported) {
          Linking.openURL(websiteUrl);
        } else {
          showAlert('Error', 'Unable to open website');
        }
      })
      .catch(() => {
        showAlert('Error', 'Unable to open website');
      });
  };

  const handlePrivacyPolicy = () => {
    navigation.navigate('Legal', { contentType: 'privacy' });
  };

  const handleTermsOfService = () => {
    navigation.navigate('Legal', { contentType: 'terms' });
  };

  const getButtonTitle = () => {
    if (!selectedUserType) return '';
    return isResident(selectedUserType) 
      ? strings.userSelection.button.continue 
      : strings.userSelection.button.web;
  };

  const showNonResidentInfo = selectedUserType && !isResident(selectedUserType);

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <LoginHeroHeader
          title={strings.userSelection.header.title}
          subtitle={strings.userSelection.header.subtitle}
        />

        <View style={styles.content}>
          <Text style={styles.sectionSubtitle}>
            {strings.userSelection.section.subtitle}
          </Text>

          <View style={styles.optionsContainer}>
            {userTypeOptions.map((option) => (
              <TouchableOpacity
                key={option.type}
                style={[
                  styles.optionCard,
                  selectedUserType === option.type && styles.optionCardSelected
                ]}
                onPress={() => handleUserTypeSelection(option.type)}
              >
                <View style={styles.optionContent}>
                  <View style={styles.optionTextContainer}>
                    <Text style={styles.optionTitle}>{option.title}</Text>
                    <Text style={styles.optionSubtitle}>{option.subtitle}</Text>
                  </View>
                  <View style={[
                    styles.checkbox,
                    selectedUserType === option.type && styles.checkboxSelected
                  ]}>
                    {selectedUserType === option.type && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Non-resident info section */}
          {showNonResidentInfo && (
            <View style={styles.nonResidentInfo}>
              <Text style={styles.nonResidentText}>
                {strings.userSelection.section.web}
              </Text>
            </View>
          )}

          {/* Continue/Website button */}
          {selectedUserType && (
            <TouchableOpacity
              style={styles.continueButton}
              onPress={handleContinue}
            >
              <Text style={styles.continueButtonText}>
                {getButtonTitle()}
              </Text>
            </TouchableOpacity>
          )}

          {/* Legal Footer */}
          <View style={styles.legalFooter}>
            <View style={styles.legalButtonsContainer}>
              <TouchableOpacity style={styles.legalButton} onPress={handlePrivacyPolicy}>
                <Text style={styles.legalButtonText}>Privacy Policy</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.legalButton} onPress={handleTermsOfService}>
                <Text style={styles.legalButtonText}>Terms of Service</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  optionsContainer: {
    marginBottom: 20,
  },
  optionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    marginBottom: 16,
    padding: 16,
  },
  optionCardSelected: {
    borderColor: COLORS.BUTTON_PRIMARY,
    borderWidth: 2,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  optionTextContainer: {
    flex: 1,
    marginRight: 12,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    marginBottom: 4,
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  optionSubtitle: {
    fontSize: 13,
    color: COLORS.TEXT_SECONDARY,
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: COLORS.TEXT_SECONDARY,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    borderColor: COLORS.PRIMARY,
    backgroundColor: COLORS.PRIMARY,
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  nonResidentInfo: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  nonResidentText: {
    fontSize: 13,
    color: COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  continueButton: {
    backgroundColor: COLORS.BUTTON_PRIMARY,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 30,
  },
  continueButtonText: {
    color: COLORS.TEXT_WHITE,
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  legalFooter: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  legalButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  legalButton: {
    paddingHorizontal: 32,
    paddingVertical: 8,
  },
  legalButtonText: {
    fontSize: 13,
    color: '#999999',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
});
